import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Paper,
  Alert,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { api, getAuth } from "../api";
import { buildItemUrl } from "../utils/itemUrl";
import { getDisplayImageUrl } from "../utils/imageUrl";
import { formatPrice, getPriceDollars } from "../utils/price";
import { usePriceVisibility } from "../contexts/PriceVisibilityContext";
import usePageTitle from "../hooks/usePageTitle";
import PaginationControls from "./PaginationControls";

const safeText = (value) => (typeof value === "string" ? value : String(value ?? ""));
const PLACEHOLDER_TOKENS = new Set(["n/a", "unknown", "none", "null", "undefined", "misc", "for"]);

const cleanField = (value) => {
  if (value == null) return "";
  const text = String(value).replace(/\s+/g, " ").trim();
  if (!text) return "";
  if (PLACEHOLDER_TOKENS.has(text.toLowerCase())) return "";
  return text;
};

const dedupeParts = (parts) => {
  const seen = new Set();
  const out = [];
  for (const raw of parts) {
    const part = cleanField(raw);
    if (!part) continue;
    const words = part
      .toLowerCase()
      .split(/\s+/)
      .filter((word) => word && !PLACEHOLDER_TOKENS.has(word));
    if (!words.length) continue;
    const key = words.join(" ");
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(part);
  }
  return out;
};

const buildEbayQueryText = (item) => {
  const name = cleanField(item?.name);
  if (name) return name;
  return dedupeParts([
    cleanField(item?.player),
    cleanField(item?.character_name),
    cleanField(item?.set_name),
    cleanField(item?.number),
  ])
    .join(" ")
    .trim();
};

const buildEbaySearchUrl = (queryText, { includeSoldCompleted = false, sortNewest = false } = {}) => {
  if (!queryText) return "";
  const query = encodeURIComponent(queryText);
  const url = new URL(`https://www.ebay.com/sch/i.html?_nkw=${query}`);

  if (includeSoldCompleted) {
    url.searchParams.set("LH_Sold", "1");
    url.searchParams.set("LH_Complete", "1");
  }
  if (sortNewest) {
    url.searchParams.set("_sop", "10");
  }

  return url.toString();
};

const getDisplayName = (item) => cleanField(item?.name) || "Unnamed Item";

const getWishlistDate = (item) => {
  const raw =
    item?.wishlist_created_at ||
    item?.created_at ||
    item?.added_on ||
    item?.wishlistDate ||
    item?.wishlist_date ||
    "";
  const ts = new Date(raw).getTime();
  return Number.isFinite(ts) ? ts : 0;
};

const getSearchableText = (item) =>
  [
    item?.name,
    item?.brand,
    item?.category,
    item?.player,
    item?.character_name,
    item?.set_name,
    item?.series_collection,
    item?.number,
    item?.year_released,
    item?.edition_variant,
  ]
    .map(cleanField)
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const withEbaySoldCompleted = (url) => {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.includes("ebay.")) return url;
    parsed.searchParams.set("LH_Sold", "1");
    parsed.searchParams.set("LH_Complete", "1");
    return parsed.toString();
  } catch {
    return url;
  }
};

const withEpnTracking = (url, customId) => {
  if (!url) return "";

  const campid = String(process.env.REACT_APP_EPN_CAMPID || "").trim();
  const mkrid = String(process.env.REACT_APP_EPN_MKRID || "").trim();
  const toolid = String(process.env.REACT_APP_EPN_TOOLID || "").trim();
  const siteid = String(process.env.REACT_APP_EPN_SITEID || "").trim();

  try {
    const parsed = new URL(url);
    parsed.searchParams.set("mkcid", "1");
    parsed.searchParams.set("mkrid", mkrid);
    parsed.searchParams.set("siteid", siteid);
    parsed.searchParams.set("campid", campid);
    parsed.searchParams.set("customid", String(customId || ""));
    parsed.searchParams.set("toolid", toolid);
    parsed.searchParams.set("mkevt", "1");
    return parsed.toString();
  } catch {
    return url;
  }
};

const toShopEbayUrl = (item, ebayQueryText) => {
  const query = cleanField(ebayQueryText) || buildEbayQueryText(item);
  if (!query) return "";
  const baseUrl = buildEbaySearchUrl(query, { sortNewest: true });
  return withEpnTracking(baseUrl, `wishlist_shop_${item?.id ?? item?.item_id ?? ""}`);
};

const trackOutbound = (item, destination, url) => {
  if (!url) return;
  const trackingItemId = Number(item?.item_id ?? item?.id ?? 0);
  if (!Number.isFinite(trackingItemId) || trackingItemId <= 0) return;

  void api.post("/api/track/outbound", {
    item_id: trackingItemId,
    destination,
    url,
    ts: new Date().toISOString(),
  }).catch(() => {});
};

const openOutbound = (item, destination, url) => {
  if (!url) return;
  trackOutbound(item, destination, url);
  window.open(url, "_blank", "noopener,noreferrer");
};

const Wishlist = () => {
  const { showPrices: pricePreferenceEnabled, pricesLoaded } = usePriceVisibility();
  const showPrices = pricesLoaded && pricePreferenceEnabled;
  usePageTitle("Wishlist");
  const navigate = useNavigate();
  const { username: routeUsername } = useParams();

  const [currentUser, setCurrentUser] = useState(null);
  const [viewUsername, setViewUsername] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOption, setSortOption] = useState("newest");
  useEffect(() => { if (!showPrices && ["high", "low"].includes(sortOption)) setSortOption("newest"); }, [showPrices, sortOption]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [removingIds, setRemovingIds] = useState({});

  const itemsPerPage = 12;

  useEffect(() => {
    const syncUser = () => {
      const { user } = getAuth();
      setCurrentUser(user || null);

      if (!routeUsername) {
        const uname = user?.username || user?.name || user?.id;
        if (uname) {
          navigate(`/wishlist/${encodeURIComponent(uname)}`, { replace: true });
        }
        return;
      }

      setViewUsername(routeUsername);
    };

    syncUser();
    window.addEventListener("storage", syncUser);
    return () => window.removeEventListener("storage", syncUser);
  }, [navigate, routeUsername]);

  useEffect(() => {
    if (!viewUsername) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError("");
        const { data } = await api.get(`/api/wishlist/${encodeURIComponent(viewUsername)}`, {
          params: {
            page: 1,
            limit: 500,
          },
        });
        if (cancelled) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
        setTotalItems(Number(data?.total || 0));
      } catch (err) {
        if (cancelled) return;
        const status = Number(err?.response?.status || 0);
        if (status === 403 && err?.response?.data?.error === "private_account") {
          setError("This profile is private. Only the collector, accepted friends, and administrators can view this wishlist.");
        } else if (status === 404) {
          setError("Wishlist not found.");
        } else {
          setError("This wishlist could not be loaded.");
        }
        setItems([]);
        setTotalItems(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [viewUsername]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortOption]);

  const handleRemove = async (itemId) => {
    if (!canManageWishlist) {
      return;
    }

    const user = currentUser || getAuth().user || null;
    const userId = user?.id ?? user?.user_id ?? null;
    if (!userId) {
      navigate("/login");
      return;
    }

    const numericItemId = Number(itemId);
    if (!Number.isFinite(numericItemId) || numericItemId <= 0) return;

    setRemovingIds((prev) => ({ ...prev, [numericItemId]: true }));
    try {
      await api.delete(`/api/wishlist/remove/${numericItemId}`);
      setItems((prev) => prev.filter((item) => Number(item.item_id) !== numericItemId));
      setTotalItems((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Wishlist remove failed:", err);
      alert("Could not remove this item from your wishlist.");
    } finally {
      setRemovingIds((prev) => {
        const next = { ...prev };
        delete next[numericItemId];
        return next;
      });
    }
  };

  const filteredItems = items.filter((item) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    return getSearchableText(item).includes(query);
  });

  const sortedItems = [...filteredItems].sort((a, b) => {
    if (sortOption === "oldest") return getWishlistDate(a) - getWishlistDate(b);
    if (sortOption === "high") return (Number(getPriceDollars(b)) || 0) - (Number(getPriceDollars(a)) || 0);
    if (sortOption === "low") return (Number(getPriceDollars(a)) || 0) - (Number(getPriceDollars(b)) || 0);
    if (sortOption === "az") return getDisplayName(a).localeCompare(getDisplayName(b), "en", { sensitivity: "base" });
    if (sortOption === "za") return getDisplayName(b).localeCompare(getDisplayName(a), "en", { sensitivity: "base" });
    return getWishlistDate(b) - getWishlistDate(a);
  });

  const visibleItems = sortedItems;
  const totalPages = Math.max(1, Math.ceil(visibleItems.length / itemsPerPage));
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const currentItems = visibleItems.slice(indexOfFirstItem, indexOfFirstItem + itemsPerPage);

  useEffect(() => {
    setCurrentPage((page) => Math.min(Math.max(1, page), totalPages));
  }, [totalPages]);

  const currentUserName = currentUser?.username || currentUser?.name || currentUser?.id || viewUsername;
  const canManageWishlist = Boolean(
    routeUsername &&
      currentUser &&
      String(currentUser.username || currentUser.name || currentUser.id || "").toLowerCase() ===
        String(routeUsername).toLowerCase()
  );

  return (
    <Box sx={{ maxWidth: 1200, margin: "auto", padding: 4 }}>
      <Paper
        sx={{
          mt: { xs: 2, sm: 3 },
          mb: 3,
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          border: "1px solid rgba(0,0,0,0.08)",
          boxShadow: "0 8px 24px rgba(15, 26, 46, 0.08)",
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
          Wishlist
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Items you want to track before adding them to your collection.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Tracking {totalItems} item{totalItems === 1 ? "" : "s"}.
        </Typography>
        {currentUserName && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Viewing for {safeText(viewUsername || currentUserName)}
          </Typography>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ maxWidth: 1440, mx: "auto", mb: 2 }}>
          {safeText(error)}
        </Alert>
      )}

      {items.length > 0 && (
        <Paper
          sx={{
            mb: 3,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            border: "1px solid rgba(0,0,0,0.08)",
            boxShadow: "0 6px 20px rgba(15, 26, 46, 0.06)",
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={7}>
              <TextField
                label="Search wishlist"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort Items</InputLabel>
                <Select
                  value={sortOption}
                  label="Sort Items"
                  onChange={(event) => setSortOption(event.target.value)}
                >
                  <MenuItem value="newest">Newest Added</MenuItem>
                  <MenuItem value="oldest">Oldest Added</MenuItem>
                  {showPrices && <MenuItem value="high">Highest Value</MenuItem>}
                  {showPrices && <MenuItem value="low">Lowest Value</MenuItem>}
                  <MenuItem value="az">A-Z</MenuItem>
                  <MenuItem value="za">Z-A</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
            Showing {sortedItems.length} of {items.length} loaded items
          </Typography>
        </Paper>
      )}

      {loading ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : items.length ? (
        sortedItems.length ? (
        <>
          <Grid container spacing={3}>
            {currentItems.map((item) => {
              const priceDollars = getPriceDollars(item);
              const isRemoving = Boolean(removingIds[Number(item.item_id)]);
              const ebayQueryText = buildEbayQueryText(item);
              const shopUrl = toShopEbayUrl(item, ebayQueryText);
              const soldUrl = withEpnTracking(
                withEbaySoldCompleted(buildEbaySearchUrl(ebayQueryText, { includeSoldCompleted: true })),
                `wishlist_sold_${item?.id ?? item?.item_id ?? ""}`
              );
              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={item.item_id}
                >
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={getDisplayImageUrl(item)}
                      alt={item.name || "Item"}
                      sx={{ objectFit: "contain", backgroundColor: "#f8f8f8" }}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>
                        {item.name || "Unnamed Item"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {item.brand || item.category || "Collectible"}
                      </Typography>
                      {showPrices && <Typography variant="subtitle1" sx={{ color: "primary.main", fontWeight: 700 }}>
                        Current Value: {formatPrice({ price: priceDollars != null ? priceDollars.toFixed(2) : null }) || "TBD"}
                      </Typography>}
                    </CardContent>
                    <CardActions sx={{ mt: "auto", px: 2, pb: 2 }}>
                      <Box
                        sx={{
                          width: "100%",
                          backgroundColor: "#F1F4F7",
                          borderRadius: 3,
                          border: "1px solid rgba(0,0,0,0.05)",
                          p: 2,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "stretch",
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                            gap: 1,
                            width: "100%",
                          }}
                        >
                          {canManageWishlist && (
                            <Button
                              variant="outlined"
                              color="error"
                              disabled={isRemoving}
                              onClick={() => handleRemove(item.item_id)}
                              sx={{ width: "100%", height: 42 }}
                            >
                              {isRemoving ? "Removing..." : "Remove"}
                            </Button>
                          )}
                          <Button
                            component={RouterLink}
                            to={buildItemUrl(item)}
                            variant="contained"
                            color="primary"
                            sx={{ width: "100%", height: 42 }}
                          >
                            View Item
                          </Button>
                          <Button
                            variant="contained"
                            color="warning"
                            disabled={!shopUrl}
                            onClick={() => openOutbound(item, "ebay_shop", shopUrl)}
                            sx={{ width: "100%", height: 42 }}
                          >
                            Shop on eBay
                          </Button>
                          {showPrices && <Button
                            variant="contained"
                            color="info"
                            disabled={!soldUrl}
                            onClick={() => openOutbound(item, "ebay_comps", soldUrl)}
                            sx={{ width: "100%", height: 42 }}
                          >
                            View Sold
                          </Button>}
                        </Box>
                      </Box>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Box sx={{ maxWidth: 1440, mx: "auto", mt: 3 }}>
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </Box>
        </>
        ) : (
          <Paper
            sx={{
              maxWidth: 860,
              mx: "auto",
              p: { xs: 3, sm: 4 },
              textAlign: "center",
              borderRadius: 3,
              border: "1px solid rgba(0,0,0,0.08)",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              No wishlist items match your search.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Try a different term or clear the search to see everything again.
            </Typography>
            <Button variant="contained" onClick={() => setSearchQuery("")}>
              Clear Search
            </Button>
          </Paper>
        )
      ) : (
        <Paper
          sx={{
            maxWidth: 860,
            mx: "auto",
            p: { xs: 3, sm: 4 },
            textAlign: "center",
            borderRadius: 3,
            border: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Your wishlist is empty.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Save items here while you track prices and decide what to add next.
          </Typography>
          <Button variant="contained" component={RouterLink} to="/search?q=__all__">
            Browse Items
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default Wishlist;
